import { Injectable } from '@angular/core';
import { AuthService, DataService } from '@telespot/web-core';
import { AnalysisState, Case, Query, Sample, SecondOpinion, Workspace } from '@telespot/sdk';
import { LoggerService } from '@telespot/shared/logger/feature/util';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { shareReplay, skipWhile, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CaseAnalysisService {
  // Case
  private _caseB$ = new BehaviorSubject<Case>(undefined);
  public readonly case$ = this._caseB$.asObservable().pipe(skipWhile((_case) => !_case));

  // Analysis state
  public readonly allUsersStates$: Observable<AnalysisState[]> = this.case$.pipe(
    switchMap((_case) => this._getUserStates(_case)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Second Opinion
  readonly secondOpinions$ = this.case$.pipe(switchMap(() => this._loadSecondOpinions()));

  constructor(
    private _logger: LoggerService,
    private _dataService: DataService,
    private _authService: AuthService
  ) {}

  public getWorkspace(id: string) {
    return from(this._dataService.first(new Query(Workspace).equalTo('objectId', id).include('caseType.fields')));
  }

  // Change case status in other screens
  public setCase(_case: Case) {
    this._logger.info(`Loading case "${_case?.name}"`);
    this._caseB$.next(_case);
  }

  private _loadSecondOpinions(): Promise<SecondOpinion[]> {
    return this._authService.currentOrganizationValue?.license?.features?.second_opinion
      ? this._dataService.find(
          new Query(SecondOpinion)
            .equalTo('createdBy', this._authService.currentUser)
            .matchesQuery('sample', new Query(Sample).equalTo('case', this._caseB$.value))
            .include(['sentTo', 'sample', 'analysisTypes'])
        )
      : Promise.resolve([]);
  }

  private _getUserStates(_case: Case): Observable<AnalysisState[]> {
    if (!_case) return new Observable<undefined>();
    return from(
      this._dataService.find(
        new Query(AnalysisState).equalTo('case', _case).doesNotExist('sample').include('user').ascending('state')
      )
    );
  }
}
